import { useCallback, useEffect, useRef, useState } from "react";

import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, PlayCircle, QrCode, Smartphone } from "lucide-react";

import { getAPIPayload } from "../../../apis/getApi";
import { postAPIPayload } from "../../../apis/postApi";
import {
  applyEpAwareInviteArtifacts,
  buildBillingPrefillFromEpAwareReport,
  clearEpAwareSignupSessionId,
  getEpAwareSignupSessionId,
  getSignupUserId,
  isIosApp,
  isNativeAppWebView,
  persistEpAwareSignupArtifacts,
  persistSignupBillingPrefill,
  persistSignupUserId,
  sanitizePropertyAddressForEpAware,
  user_signup_details,
} from "../../helpers/utils";
import CustomPhoneNumberInput from "../../common/CustomPhoneNumberInput";
import { Button } from "../../common/customDesignedComponents/button";
import { resolveUsCellPhone } from "../../helpers/usCellPhone";
import PropertySelectionFromId from "./PropertySelectionFromId";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";

type EpAwareStartContext = {
  userId: number | string;
  propertyId?: string;
  propertyAddress: Record<string, unknown>;
  partyEmail?: string;
  partyPhone?: string;
  partyName?: string;
};

type EpAwareIdVerificationProps = {
  onBack: () => void;
  buildVerifyPayload: () => Record<string, unknown>;
  onPropertyProtected?: () => void;
  onSearchDifferentAddress?: (
    sessionId: string,
    idMailingAddress: {
      address_line?: string;
      city?: string;
      state?: string;
      zip?: string;
    } | null,
  ) => void;
  startContext?: EpAwareStartContext;
  useAuthToken?: boolean;
  onApplySuccess?: (response: unknown) => void;
  autoStart?: boolean;
};

type Phase = "qr" | "property_selection";

const LOADING_TIMEOUT_MS = 30000;

const isMobileNativeClient = () => isNativeAppWebView() || isIosApp();

const openVerificationOnDevice = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed) {
    return;
  }

  window.open(trimmed, "_blank", "noopener,noreferrer");
};

const readSessionSignupDetails = () => {
  try {
    const stored = sessionStorage.getItem("userSignupDetails");
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored);
    return parsed?.data && typeof parsed.data === "object"
      ? parsed.data
      : parsed;
  } catch {
    return null;
  }
};

const EpAwareIdVerification = ({
  onBack,
  buildVerifyPayload,
  onPropertyProtected,
  onSearchDifferentAddress,
  startContext,
  useAuthToken = false,
  onApplySuccess,
  autoStart = true,
}: EpAwareIdVerificationProps) => {
  const dispatch = useDispatch();
  const pollRef = useRef<number | null>(null);
  const loadingTimeoutRef = useRef<number | null>(null);
  const startVerificationRef = useRef<(() => Promise<void>) | null>(null);
  const verifyUrlOpenedRef = useRef(false);
  const mobileNativeClient = isMobileNativeClient();
  const [phase, setPhase] = useState<Phase>("qr");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState(
    mobileNativeClient
      ? "Opening secure ID verification on this device..."
      : "Scan the QR code with your phone to verify your ID.",
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [verifyUrl, setVerifyUrl] = useState("");
  const [qrCodeDataUri, setQrCodeDataUri] = useState("");
  const [enteredPhone, setEnteredPhone] = useState("");
  const enteredPhoneRef = useRef("");
  const storedUsCellPhoneRef = useRef("");

  const {
    createAccResponse,
    selectedProperty,
    propertyQuestion,
    user_id,
    userSignupDetails,
    emailOTPVerificationResponse,
  } = useSelector((state: any) => state.signUpUser);

  const signupDetailsForPhone = user_signup_details() || {};
  const sessionSignupDetailsForPhone = readSessionSignupDetails() || {};
  const nestedSignupDetailsForPhone = userSignupDetails?.data || {};
  const storedUsCellPhone = resolveUsCellPhone(
    startContext?.partyPhone,
    signupDetailsForPhone.phone,
    signupDetailsForPhone.phone1,
    signupDetailsForPhone.phone_number,
    sessionSignupDetailsForPhone.phone,
    sessionSignupDetailsForPhone.phone1,
    sessionSignupDetailsForPhone.phone_number,
    nestedSignupDetailsForPhone.phone,
    nestedSignupDetailsForPhone.phone1,
    nestedSignupDetailsForPhone.phone_number,
    createAccResponse?.data?.phone,
    createAccResponse?.data?.phone_number,
    emailOTPVerificationResponse?.activity?.phone,
  );
  storedUsCellPhoneRef.current = storedUsCellPhone;

  useEffect(() => {
    // In the add-property / admin context useAuthToken is true and the user ID
    // is already provided via startContext — dispatching hydrateSignupUserId here
    // would update createAccResponse in Redux and accidentally trigger screen-level
    // effects in parent pages (e.g. AddNewUserPage setScreen("security")), which
    // kicks the user back to the property-search step.  Only hydrate when we are
    // in the self-signup flow (useAuthToken === false).
    if (useAuthToken) {
      return;
    }
    const resolvedUserId = getSignupUserId();
    if (resolvedUserId) {
      dispatch(userSigupAction.hydrateSignupUserId(resolvedUserId));
    }
  }, [dispatch, useAuthToken]);

  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  const beginLoading = useCallback(() => {
    clearLoadingTimeout();
    setLoading(true);
    loadingTimeoutRef.current = window.setTimeout(() => {
      setLoading(false);
      setError(
        "This is taking longer than expected. Check that the API server is running at http://127.0.0.1:8000, then try again.",
      );
    }, LOADING_TIMEOUT_MS);
  }, [clearLoadingTimeout]);

  const endLoading = useCallback(() => {
    clearLoadingTimeout();
    setLoading(false);
  }, [clearLoadingTimeout]);

  const storeInviteArtifacts = useCallback(
    (
      sessionIdValue: string | number | null | undefined,
      verifyUrlValue?: string,
      qrCodeDataUriValue?: string,
    ) => {
      persistEpAwareSignupArtifacts({
        sessionId: sessionIdValue,
        verifyUrl: verifyUrlValue,
        qrCodeDataUri: qrCodeDataUriValue,
      });
      return applyEpAwareInviteArtifacts({
        verifyUrl: verifyUrlValue,
        qrCodeDataUri: qrCodeDataUriValue,
        setVerifyUrl,
        setQrCodeDataUri,
      });
    },
    [],
  );

  const startVerification = useCallback(async () => {
    const partyPhone =
      storedUsCellPhoneRef.current ||
      resolveUsCellPhone(enteredPhoneRef.current);
    if (!partyPhone) {
      setError("Enter a valid US cell phone number.");
      return;
    }

    beginLoading();
    setError("");
    setPhase("qr");
    verifyUrlOpenedRef.current = false;

    try {
      const signupDetails = user_signup_details() || {};
      const sessionSignupDetails = readSessionSignupDetails() || {};
      const nestedSignupDetails = userSignupDetails?.data || {};
      const userId =
        startContext?.userId ??
        createAccResponse?.data?.user_id ??
        user_id ??
        getSignupUserId() ??
        signupDetails?.user_id ??
        nestedSignupDetails?.user_id ??
        emailOTPVerificationResponse?.activity?.user_id ??
        null;

      if (!userId) {
        throw new Error(
          "We could not find your account details. Please sign in again.",
        );
      }

      if (!startContext) {
        persistSignupUserId(userId);
      }

      const rawPropertyAddress = startContext?.propertyAddress || {
        ...(selectedProperty || {}),
        ...(propertyQuestion?.data || {}),
      };
      const propertyAddress =
        sanitizePropertyAddressForEpAware(rawPropertyAddress);

      const partyEmail =
        startContext?.partyEmail ??
        signupDetails?.email ??
        sessionSignupDetails?.email ??
        nestedSignupDetails?.email ??
        createAccResponse?.data?.email ??
        emailOTPVerificationResponse?.activity?.email ??
        undefined;
      const propertyId =
        startContext?.propertyId ??
        selectedProperty?.property_id ??
        propertyQuestion?.data?.property_id;
      const resolvedPropertyId =
        propertyId === null || propertyId === undefined || propertyId === ""
          ? undefined
          : String(propertyId);

      const response = await postAPIPayload({
        url: "properties/ep-aware-verification/start",
        data: {
          user_id: userId,
          property_id: resolvedPropertyId,
          property_address: propertyAddress,
          party_email: partyEmail,
          party_phone: partyPhone,
          party_name:
            startContext?.partyName ??
            (signupDetails?.first_name
              ? `${signupDetails.first_name} ${signupDetails.last_name || ""}`.trim()
              : createAccResponse?.data?.first_name || undefined),
        },
        token: useAuthToken,
      });

      if (!response?.success) {
        const validationErrors = response?.data?.errors;
        const firstValidationError =
          validationErrors && typeof validationErrors === "object"
            ? Object.values(validationErrors).flat().find(Boolean)
            : null;

        throw new Error(
          firstValidationError ||
            response?.data?.message ||
            response?.message ||
            "Unable to start EpAware verification.",
        );
      }

      const nextSessionId = response.data?.session_id;
      const nextVerifyUrl = response.data?.verify_url || "";
      const nextQrCodeDataUri = response.data?.qr_code_data_uri || "";

      if (!nextSessionId || (!nextVerifyUrl && !nextQrCodeDataUri)) {
        throw new Error(
          "EpAware did not return a verification link. Please try again.",
        );
      }

      setSessionId(String(nextSessionId));
      storeInviteArtifacts(nextSessionId, nextVerifyUrl, nextQrCodeDataUri);
      if (mobileNativeClient && nextVerifyUrl) {
        if (!verifyUrlOpenedRef.current) {
          verifyUrlOpenedRef.current = true;
          openVerificationOnDevice(nextVerifyUrl);
        }
        setStatusMessage(
          "Finish ID verification in your browser, then return here.",
        );
      } else {
        setStatusMessage("Scan the QR code with your phone to verify your ID.");
      }
    } catch (startError: any) {
      setError(startError?.message || "Unable to start EpAware verification.");
    } finally {
      endLoading();
    }
  }, [
    beginLoading,
    createAccResponse,
    emailOTPVerificationResponse,
    endLoading,
    propertyQuestion,
    selectedProperty,
    startContext,
    storeInviteArtifacts,
    useAuthToken,
    userSignupDetails,
    user_id,
  ]);

  startVerificationRef.current = startVerification;

  const resumeStoredSession = useCallback(async () => {
    const storedSessionId = getEpAwareSignupSessionId();
    if (!storedSessionId) {
      return false;
    }

    beginLoading();
    setError("");
    setPhase("qr");
    setSessionId(storedSessionId);

    try {
      const response = await getAPIPayload({
        url: `properties/ep-aware-verification/${storedSessionId}/status`,
        token: useAuthToken,
      });

      if (!response?.success) {
        clearEpAwareSignupSessionId();
        return false;
      }

      if (response.data?.report) {
        const idPrefill = buildBillingPrefillFromEpAwareReport(
          response.data.report,
        );
        if (idPrefill) {
          persistSignupBillingPrefill(idPrefill);
        }
      }

      const status = response.data?.status;
      if (
        response.data?.property_selection_required ||
        response.data?.ready_to_apply
      ) {
        setPhase("property_selection");
        return true;
      }

      if (
        status === "failed" ||
        status === "expired" ||
        status === "cancelled"
      ) {
        clearEpAwareSignupSessionId();
        return false;
      }

      if (status === "pending" || status === "processing") {
        const artifacts = storeInviteArtifacts(
          storedSessionId,
          response.data?.verify_url,
          response.data?.qr_code_data_uri,
        );

        if (!artifacts.verifyUrl && !artifacts.qrCodeDataUri) {
          clearEpAwareSignupSessionId();
          return false;
        }

        setStatusMessage(
          status === "processing"
            ? "ID received. Finishing verification on our servers..."
            : mobileNativeClient
              ? "Finish ID verification in your browser, then return here."
              : "Scan the QR code with your phone to verify your ID.",
        );
        return true;
      }

      clearEpAwareSignupSessionId();
      return false;
    } catch {
      clearEpAwareSignupSessionId();
      return false;
    } finally {
      endLoading();
    }
  }, [beginLoading, endLoading, storeInviteArtifacts, useAuthToken]);

  useEffect(() => {
    return () => {
      clearLoadingTimeout();
    };
  }, [clearLoadingTimeout]);

  useEffect(() => {
    if (!autoStart) {
      return undefined;
    }

    let cancelled = false;

    const initializeVerification = async () => {
      const resumed = await resumeStoredSession();
      if (cancelled) {
        return;
      }
      if (!resumed && storedUsCellPhoneRef.current) {
        await startVerificationRef.current?.();
      }
    };

    initializeVerification();

    return () => {
      cancelled = true;
    };
    // Run once when this screen opens. Do not re-run when Redux updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const pollStatus = useCallback(async () => {
    if (!sessionId || phase !== "qr") {
      return;
    }

    try {
      const response = await getAPIPayload({
        url: `properties/ep-aware-verification/${sessionId}/status`,
        token: useAuthToken,
      });

      if (!response?.success) {
        return;
      }

      const status = response.data?.status;
      if (response.data?.report) {
        const idPrefill = buildBillingPrefillFromEpAwareReport(
          response.data.report,
        );
        if (idPrefill) {
          persistSignupBillingPrefill(idPrefill);
        }
      }

      if (status === "pending") {
        setStatusMessage(
          mobileNativeClient
            ? "Finish ID verification in your browser, then return here."
            : "Waiting for you to finish ID verification on your phone...",
        );
        return;
      }

      if (status === "processing") {
        setStatusMessage(
          "ID received. Finishing verification on our servers...",
        );
        return;
      }

      if (
        response.data?.property_selection_required ||
        response.data?.ready_to_apply
      ) {
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
        }
        setPhase("property_selection");
        return;
      }

      if (status === "failed" || status === "expired") {
        setError(
          response.data?.failure_reason ||
            "EpAware could not complete your identity verification.",
        );
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
        }
      }
    } catch (pollError: any) {
      setError(pollError?.message || "Unable to check verification status.");
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
      }
    }
  }, [phase, sessionId, useAuthToken]);

  useEffect(() => {
    if (!autoStart || !sessionId || error || phase !== "qr") {
      return undefined;
    }

    pollRef.current = window.setInterval(pollStatus, 4000);
    pollStatus();

    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
      }
    };
  }, [autoStart, error, phase, pollStatus, sessionId]);

  if (phase === "property_selection" && sessionId) {
    return (
      <PropertySelectionFromId
        sessionId={sessionId}
        buildVerifyPayload={buildVerifyPayload}
        onBack={() => {
          clearEpAwareSignupSessionId();
          window.scrollTo({ top: 0, behavior: "smooth" });
          onBack();
        }}
        onProtected={() => {
          clearEpAwareSignupSessionId();
          onPropertyProtected?.();
        }}
        onSearchDifferentAddress={onSearchDifferentAddress}
        useAuthToken={useAuthToken}
        onApplySuccess={onApplySuccess}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary/10 text-secondary mb-4">
          <QrCode className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-display font-medium text-foreground mb-2">
          Verify with your ID
        </h2>
        <p className="text-sm text-muted-foreground">
          {mobileNativeClient
            ? "We will open secure ID verification on this device. Once EpAware confirms your ID, we will help you choose which properties to protect."
            : "Scan the QR code with your phone, complete the EpAware ID check, and we will help you choose which properties to protect."}
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!storedUsCellPhone && !qrCodeDataUri && !loading && (
        <div className="mx-auto max-w-md space-y-2 text-left">
          <p className="text-sm text-muted-foreground">
            Enter the US cell phone number for this ID check.
          </p>
          <CustomPhoneNumberInput
            name="phone"
            placeholder="Cell phone"
            isdefaultCountryUS
            values={{ phone: enteredPhone }}
            touched={{}}
            errors={{}}
            setFieldValue={(_name, value) => {
              const next = value || "";
              enteredPhoneRef.current = next;
              setEnteredPhone(next);
            }}
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          Preparing your secure verification link...
        </div>
      ) : (
        <>
          {qrCodeDataUri && !mobileNativeClient ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-4"
            >
              <div className="bg-white p-4 rounded-2xl border border-border shadow-sm">
                <img
                  src={qrCodeDataUri}
                  alt="EpAware ID verification QR code"
                  className="w-56 h-56 object-contain"
                />
              </div>
              {verifyUrl && (
                <a
                  href={verifyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-secondary text-sm font-medium hover:underline break-all text-center"
                >
                  Open verification link on this device
                </a>
              )}
            </motion.div>
          ) : null}

          {mobileNativeClient && verifyUrl ? (
            <div className="flex justify-center">
              <Button
                type="button"
                onClick={() => openVerificationOnDevice(verifyUrl)}
              >
                Continue ID verification
              </Button>
            </div>
          ) : null}

          <div className="rounded-xl border border-border bg-muted/20 p-4 flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">
                {statusMessage}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {mobileNativeClient
                  ? "Keep this page open while you finish verification. We will show property options automatically once EpAware confirms your ID."
                  : "Keep this page open while you finish on your phone. We will show property options automatically once EpAware confirms your ID."}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                clearEpAwareSignupSessionId();
                window.scrollTo({ top: 0, behavior: "smooth" });
                onBack();
              }}
            >
              Choose another method
            </Button>
            {(error || (!qrCodeDataUri && !loading)) && (
              <Button
                type="button"
                onClick={() => {
                  clearEpAwareSignupSessionId();
                  startVerification();
                }}
              >
                {error ? "Try again" : "Generate QR code"}
              </Button>
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-border bg-muted/40">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:gap-5 sm:p-5">
              <div className="min-w-0 flex-1 space-y-1.5 sm:pr-2">
                <div className="flex items-center gap-2 text-secondary">
                  <PlayCircle className="h-5 w-5 shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Quick guide
                  </span>
                </div>
                <h3 className="font-display text-base font-medium text-foreground">
                  Need help verifying your ID?
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {mobileNativeClient
                    ? "Tap Continue ID verification to open EpAware in your browser and complete the check on this device."
                    : "Watch this short walkthrough to see how to scan the QR code and complete EpAware ID verification on your phone."}
                </p>
              </div>
              {!mobileNativeClient ? (
                <div className="mx-auto w-full max-w-[360px] shrink-0 overflow-hidden rounded-xl border border-border bg-background shadow-sm sm:mx-0 sm:max-w-[380px]">
                  <iframe
                    className="aspect-video w-full"
                    src="https://www.youtube.com/embed/4qGw3ZhjP5U?modestbranding=1&rel=0&showinfo=0"
                    title="How to verify with your ID"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EpAwareIdVerification;
